// The « Réserver une démo » form, shared by the home page and the feature
// landing pages (HomePage.jsx). Posts to /public/book-demo with the
// ambassador's referral code (?ref=) and where the visitor came from
// (utils/attribution.js), which the backend keeps on the commercial lead.
import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAttribution } from '../../utils/attribution';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://192.168.1.17:8080';

const DEMO_FORM_INITIAL = {
  business_name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  city: '',
};

export default function BookDemoDialog({ open, onClose, referralCode }) {
  const { t } = useTranslation();
  const [demoForm, setDemoForm] = useState(DEMO_FORM_INITIAL);
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoResult, setDemoResult] = useState(null); // 'success' | 'error' | null

  const closeDemoDialog = () => {
    if (demoSubmitting) return;
    onClose();
    setDemoResult(null);
    setDemoForm(DEMO_FORM_INITIAL);
  };

  const submitDemoRequest = async (event) => {
    event.preventDefault();
    setDemoSubmitting(true);
    setDemoResult(null);

    try {
      const res = await fetch(`${API_BASE_URL}/public/book-demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...demoForm,
          referral_code: referralCode || undefined,
          attribution: getAttribution() || undefined,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setDemoResult('success');
    } catch (err) {
      console.error('[book demo] failed:', err);
      setDemoResult('error');
    } finally {
      setDemoSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={closeDemoDialog} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>{t('home.bookDemo')}</DialogTitle>

      {demoResult === 'success' ? (
        <>
          <DialogContent>
            <Alert severity="success">{t('home.bookDemoDialog.success')}</Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={closeDemoDialog} variant="contained">
              {t('home.bookDemoDialog.close')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <Box component="form" onSubmit={submitDemoRequest}>
          <DialogContent>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('home.bookDemoDialog.subtitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {t('legalPages.demoNotice')}{' '}
              <Link component={RouterLink} to="/confidentialite">{t('legalPages.privacy')}</Link>
            </Typography>

            {referralCode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('home.bookDemoDialog.referralNote')}
              </Alert>
            )}

            <Stack spacing={2}>
              <TextField
                label={t('home.bookDemoDialog.businessName')}
                required
                fullWidth
                value={demoForm.business_name}
                onChange={(e) => setDemoForm((s) => ({ ...s, business_name: e.target.value }))}
              />
              <TextField
                label={t('home.bookDemoDialog.contactName')}
                required
                fullWidth
                value={demoForm.contact_name}
                onChange={(e) => setDemoForm((s) => ({ ...s, contact_name: e.target.value }))}
              />
              <TextField
                label={t('home.bookDemoDialog.email')}
                type="email"
                required
                fullWidth
                value={demoForm.contact_email}
                onChange={(e) => setDemoForm((s) => ({ ...s, contact_email: e.target.value }))}
              />
              <TextField
                label={t('home.bookDemoDialog.phone')}
                fullWidth
                value={demoForm.contact_phone}
                onChange={(e) => setDemoForm((s) => ({ ...s, contact_phone: e.target.value }))}
              />
              <TextField
                label={t('home.bookDemoDialog.city')}
                fullWidth
                value={demoForm.city}
                onChange={(e) => setDemoForm((s) => ({ ...s, city: e.target.value }))}
              />
            </Stack>

            {demoResult === 'error' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t('home.bookDemoDialog.error')}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={closeDemoDialog} disabled={demoSubmitting}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={demoSubmitting}
              startIcon={demoSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {demoSubmitting ? t('home.bookDemoDialog.submitting') : t('home.bookDemoDialog.submit')}
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
}
